import { Component } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import * as moment from 'moment/moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';

import { environment } from '@env/environment';
import { FileType } from '@shared/components/files-input/files-input.component';
import { FilesSubmitValidateComponent } from '@shared/components/files-submit-validate/files-submit-validate.component';
import { BlurOnModalCloseDirective } from '@shared/directives/blur-on-modal-close.directive';
import { AlertModalComponent } from '@shared/layouts';
import { ImportOrderScheduleData } from '@shared/models';
import { AuthGuardService } from '@shared/services';
import { StoreService } from '@shared/services/store.service';
import { SharedModule } from '@shared/shared.module';
import { LayoutActionLoadError } from '@shared/store/actions/layout.action';
import { AppStates } from '@shared/store/state/app.states';
import { b64toBlob } from '@shared/utils/b64toBlob-util';

@Component({
  selector: 'app-store-import-order-schedule',
  standalone: true,
  imports: [SharedModule, BlurOnModalCloseDirective],
  templateUrl: './store-import-order-schedule.component.html',
  styleUrl: './store-import-order-schedule.component.scss'
})
export class StoreImportOrderScheduleComponent extends OnDestroyMixin {
  public filesSubmitModal: BsModalRef;
  public errorFile: Blob;
  public validatedData: ImportOrderScheduleData[] = [];

  get timeError(): string {
    return moment().format(environment.fileName.exportStoreOrderScheduleTemplate.timeFormat);
  }

  constructor(
    protected readonly store: Store<AppStates>,
    protected readonly modalService: BsModalService,
    protected authGuardService: AuthGuardService,
    protected readonly translate: TranslateService,
    protected storeService: StoreService
  ) {
    super();
  }

  hasImportPermission(): boolean {
    return this.authGuardService.checkPermission(['merchant_order_m']);
  }

  onOpenImportOrderSchedule() {
    this.filesSubmitModal = this.modalService.show(FilesSubmitValidateComponent, {
      backdrop: 'static',
      keyboard: false,
      initialState: {
        title: 'Import',
        templateName: 'Store Order Schedule Template',
        fileTitle: 'Store Order Schedule',
        okTitle: 'Submit',
        validateRule: {
          limit: 15_000,
          totalColumnsNumber: 8,
          format: [FileType.XLSX],
          exactColumnOrder: [
            'Store Code',
            'Store Name',
            'Schedule Type',
            'Warehouse Code',
            'Warehouse Name',
            'Order Schedule',
            'Pick Schedule',
            'Delivery Schedule'
          ]
        }
      }
    });

    this.filesSubmitModal.content.downloadTemplate.pipe(untilComponentDestroyed(this)).subscribe(() =>
      this.storeService.downloadStoreOrderScheduleTemplate().subscribe({
        next: response => {
          const blob = new Blob([response]);
          saveAs(blob, `${environment.fileName.exportStoreOrderScheduleTemplate.prefix}.xlsx`);
        },
        error: error => {
          this.modalService.show(AlertModalComponent, {
            initialState: {
              title: 'Failed',
              message: error.error.message
            }
          });
        }
      })
    );

    this.filesSubmitModal.content.upload.pipe(untilComponentDestroyed(this)).subscribe((file: File) => {
      this.storeService.importFileOrderSchedule(file).subscribe({
        next: res => {
          this.filesSubmitModal.content.isValidateSuccess = true;
          this.validatedData = res;
        },
        error: errorResponse => {
          const errorMessage = errorResponse.error.fileBase64String
            ? {
                errorMessage: `${errorResponse.error.rowErrorNo} error found. Download this file to edit or delete invalid row then re-import.`
              }
            : { errorMessage: this.translateErrorFromImport(errorResponse) };

          this.filesSubmitModal.content.setErrorMessage(errorMessage, Boolean(errorResponse.error.fileBase64String));

          if (errorResponse.error.fileBase64String) {
            this.errorFile = b64toBlob(errorResponse.error.fileBase64String);
          }
        }
      });
    });

    this.filesSubmitModal.content.submit.pipe(untilComponentDestroyed(this)).subscribe(() => {
      this.submit();
    });

    this.filesSubmitModal.content.downloadErrorExcel.pipe(untilComponentDestroyed(this)).subscribe(() => {
      this.downloadErrorExcel(
        `${environment.fileName.exportStoreOrderScheduleTemplate.exported} Error ${this.timeError}.xlsx`
      );
    });
  }

  downloadErrorExcel(fileName: string) {
    saveAs(this.errorFile, fileName);
  }

  submit() {
    this.storeService.importStoreOrderSchedule(this.validatedData).subscribe({
      next: () => {
        this.validatedData = null;
        this.filesSubmitModal.content.closeModal();
        this.filesSubmitModal = null;
        this.alertSuccessModal('The data have been imported.');
      },
      error: errorResponse => {
        this.filesSubmitModal.content.isValidateSuccess = false;
        if (errorResponse.status === 400) {
          this.filesSubmitModal.content.setErrorMessage(
            { errorMessage: this.translateErrorFromImport(errorResponse) },
            false
          );
        } else {
          this.store.dispatch(new LayoutActionLoadError(errorResponse));
        }
      }
    });
  }

  translateErrorFromImport(errorResponse: any) {
    let result;
    if (['00001', '00000'].includes(errorResponse.error.code)) {
      result = errorResponse.error.message;
    } else {
      result = this.translate.instant(errorResponse.error.translateKey);
    }
    return result;
  }

  alertSuccessModal(message: string) {
    const initialState = {
      title: 'Success',
      message
    };

    this.modalService.show(AlertModalComponent, {
      initialState
    });
  }
}
